import React, { useEffect, useState } from 'react';
import Seo from '../components/Seo';
import { organizationLd, websiteLd, faqLd } from '../lib/seo';
import FaqSection from '../components/FaqSection';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { ArrowRight, MessageCircle, Ship, ShieldCheck, Quote } from 'lucide-react';
import Reveal from '../components/Reveal';
import WorldMap from '../components/WorldMap';
import HeroEditorial from '../components/HeroEditorial';
import CinematicIntro from '../components/CinematicIntro';
import TrustStrip from '../components/TrustStrip';
import { api, resolveImg } from '../lib/api';
import { products, categories, certifications, whyChoose, buyers, markets, facilityImage, globalRegions } from '../mock/mock';
import { openWhatsApp } from '../lib/wa';

const Home = () => {
  const featured = products.filter((p) => ['p1', 'p8', 'p4', 'p12'].includes(p.id));
  const [latest, setLatest] = useState([]);
  const fmt = (d) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

  useEffect(() => {
    api.get('/updates').then(({ data }) => setLatest(data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div>
      <Seo jsonLd={[organizationLd, websiteLd, faqLd]} />
      {/* ONE-TIME CINEMATIC INTRO (plays once per session, above the homepage) */}
      <CinematicIntro />
      {/* EDITORIAL HERO */}
      <HeroEditorial />

      {/* BUYER TRUST STRIP */}
      <TrustStrip />

      {/* CREDIBILITY STRIP */}
      <section className="bg-ktc-greenDark border-y border-ktc-ivory/10">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-wrap items-center justify-center gap-x-10 gap-y-3">
          {certifications.map((c) => (
            <div key={c.abbr} className="flex items-center gap-2 text-ktc-ivory/85">
              <span className="font-serif font-bold text-ktc-goldLight text-lg">{c.abbr}</span>
              <span className="text-xs text-ktc-ivory/55 hidden md:inline">Certified</span>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-6 py-20 md:py-24">
        <Reveal className="text-center max-w-2xl mx-auto">
          <span className="eyebrow">Our Range</span>
          <h2 className="font-serif text-4xl md:text-5xl font-semibold text-ktc-green mt-3 gold-underline center inline-block pb-1">Products We Export</h2>
          <p className="text-ktc-charcoal/70 mt-8">From aromatic basmati and staple rice to Himalayan Pink Salt, wheat, yellow corn maize and natural sesame seeds.</p>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
          {categories.map((c, i) => {
            const img = products.find((p) => p.category === c.id)?.image;
            return (
              <Reveal key={c.id} delay={i * 60}>
                <Link to={`/products?category=${c.id}`} className="group block card-lift bg-ktc-ivoryLight rounded-xl overflow-hidden border border-ktc-cream h-full">
                  <div className="img-zoom h-52">
                    <img src={img} alt={c.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif text-2xl font-semibold text-ktc-green">{c.name}</h3>
                    <p className="text-sm text-ktc-charcoal/70 mt-2">{c.blurb}</p>
                    <span className="inline-flex items-center gap-1.5 text-ktc-gold font-medium text-sm mt-4 group-hover:gap-2.5 transition-all">
                      Explore <ArrowRight size={16} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* OWN MILL / QUALITY STORY */}
      <section className="bg-ktc-cream/60">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-24 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal className="img-zoom rounded-2xl overflow-hidden shadow-xl">
            <img src={facilityImage} alt="Rice processing facility" className="w-full h-[440px] object-cover" />
          </Reveal>
          <Reveal delay={100}>
            <span className="eyebrow">Own Mill · Quality Control</span>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-ktc-green mt-3">Direct Mill. Certified Quality. Global Reach.</h2>
            <p className="text-ktc-charcoal/75 mt-5 leading-relaxed">
              We operate our own rice mill at Port Qasim, Karachi, giving us full control over milling,
              packing and export timelines. Every grain meets international quality standards before it ships.
            </p>
            <div className="grid sm:grid-cols-2 gap-5 mt-8">
              {whyChoose.slice(0, 4).map((w) => {
                const Icon = Icons[w.icon] || ShieldCheck;
                return (
                  <div key={w.title} className="flex gap-3">
                    <div className="h-10 w-10 shrink-0 grid place-items-center rounded-lg bg-ktc-green text-ktc-goldLight">
                      <Icon size={19} />
                    </div>
                    <div>
                      <h4 className="font-sans font-semibold text-ktc-green text-sm">{w.title}</h4>
                      <p className="text-xs text-ktc-charcoal/65 mt-1 leading-relaxed">{w.text}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <Link to="/about#mill-tour" className="inline-flex items-center gap-2 text-ktc-green font-semibold mt-8 link-underline">
              Take a mill tour <ArrowRight size={17} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* GLOBAL REACH (combined) */}
      <section className="bg-ktc-green text-ktc-ivory relative overflow-hidden">
        <div className="absolute inset-0 grain-texture opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <Reveal>
              <span className="eyebrow-light">Global Reach</span>
              <h2 className="font-serif text-4xl md:text-5xl font-semibold mt-3">Trusted by Buyers Across 30+ Countries</h2>
              <p className="text-ktc-ivory/75 mt-5 leading-relaxed">
                From our own mill at Port Qasim, we ship premium rice, Himalayan Pink Salt and more to
                importers, distributors and re-exporters across the Gulf, Africa, Asia, Europe and the
                Americas. Explore the regions we serve on our interactive map.
              </p>
              <div className="flex flex-wrap gap-2 mt-6">
                {markets.map((m) => (
                  <span key={m} className="px-4 py-2 rounded-full border border-ktc-ivory/20 text-ktc-ivory/90 text-sm hover:border-ktc-gold hover:bg-ktc-gold/10 transition-colors">{m}</span>
                ))}
              </div>
              <Link to="/global-reach" className="inline-flex items-center gap-2 bg-ktc-gold hover:bg-ktc-goldLight text-ktc-greenDark font-semibold px-7 py-3.5 rounded-md mt-8 transition-colors">
                Explore Global Reach <ArrowRight size={17} />
              </Link>
            </Reveal>
            <Reveal delay={100} className="bg-ktc-greenDark/40 rounded-2xl border border-ktc-ivory/10 p-4 md:p-6">
              <WorldMap regions={globalRegions} activeId="gcc" />
            </Reveal>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-14">
            {buyers.map((b, i) => (
              <Reveal key={b} delay={i * 40} className="flex items-center gap-3 bg-ktc-greenDark/60 border border-ktc-ivory/10 rounded-lg px-4 py-3.5">
                <ShieldCheck size={18} className="text-ktc-goldLight shrink-0" />
                <span className="text-sm text-ktc-ivory/85">{b}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRIVATE LABEL / PACKING */}
      <section className="max-w-7xl mx-auto px-6 py-20 md:py-24 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <span className="eyebrow">Private Label & Packing</span>
          <h2 className="font-serif text-4xl md:text-5xl font-semibold text-ktc-green mt-3">Custom Packing Under Your Brand</h2>
          <p className="text-ktc-charcoal/75 mt-5 leading-relaxed">
            We offer 25kg, 50kg and custom packing in PP, jute and non-woven bags, as well as branded
            consumer packs. Private label is available under your own brand name for retail and wholesale.
          </p>
          <ul className="mt-6 space-y-3">
            {['25kg, 50kg & custom packing', 'PP, jute, non-woven & consumer packs', 'Private label under your brand', 'FOB Port Qasim · CIF worldwide'].map((t) => (
              <li key={t} className="flex items-center gap-3 text-ktc-charcoal/80 text-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-ktc-gold" /> {t}
              </li>
            ))}
          </ul>
          <Link to="/quote" className="inline-flex items-center gap-2 bg-ktc-green hover:bg-ktc-greenLight text-ktc-ivory font-medium px-7 py-3.5 rounded-md mt-8 transition-colors">
            Discuss your requirement <ArrowRight size={17} />
          </Link>
        </Reveal>
        <Reveal delay={100} className="img-zoom rounded-2xl overflow-hidden shadow-xl">
          <img src="https://images.pexels.com/photos/21958120/pexels-photo-21958120.jpeg" alt="Export packing" className="w-full h-[440px] object-cover" />
        </Reveal>
      </section>

      {/* LATEST UPDATES */}
      <section className="bg-ktc-cream/60">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-24">
          <Reveal className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <span className="eyebrow">News & Updates</span>
              <h2 className="font-serif text-4xl md:text-5xl font-semibold text-ktc-green mt-3">Latest from KTC</h2>
            </div>
            <Link to="/updates" className="inline-flex items-center gap-2 text-ktc-green font-semibold link-underline">
              View all updates <ArrowRight size={17} />
            </Link>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {latest.map((u, i) => (
              <Reveal key={u.id} delay={i * 60}>
                <Link to={`/updates/${u.slug}`} className="group block bg-ktc-ivoryLight rounded-xl overflow-hidden border border-ktc-cream card-lift h-full">
                  <div className="img-zoom h-44">
                    <img src={resolveImg(u.image)} alt={u.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 text-xs">
                      <span className="bg-ktc-cream text-ktc-green font-semibold px-2.5 py-1 rounded-full">{u.category}</span>
                      <span className="text-ktc-charcoal/50">{fmt(u.date)}</span>
                    </div>
                    <h3 className="font-serif text-xl font-semibold text-ktc-green mt-3 leading-snug">{u.title}</h3>
                    <p className="text-sm text-ktc-charcoal/65 mt-2 line-clamp-2">{u.excerpt}</p>
                    <span className="inline-flex items-center gap-1.5 text-ktc-gold font-medium text-sm mt-4 group-hover:gap-2.5 transition-all">Read more <ArrowRight size={15} /></span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FaqSection />

      {/* FINAL CTA */}
      <section className="bg-ktc-cream/70">
        <div className="max-w-5xl mx-auto px-6 py-20 text-center">
          <Reveal>
            <Quote className="mx-auto text-ktc-gold" size={34} />
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-ktc-green mt-5 text-balance">
              Ready to Source Premium Rice & Salt Direct from Pakistan?
            </h2>
            <p className="text-ktc-charcoal/70 mt-5 max-w-2xl mx-auto">
              Partner with Kassam Trading Company, a REAP registered rice miller and exporter since 1999.
              FOB Port Qasim. CIF available worldwide.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <Link to="/quote" className="inline-flex items-center gap-2 bg-ktc-green hover:bg-ktc-greenLight text-ktc-ivory font-semibold px-8 py-3.5 rounded-md transition-colors">
                Request a Quote <ArrowRight size={18} />
              </Link>
              <button type="button" onClick={() => openWhatsApp()} data-testid="home-cta-whatsapp-button" className="inline-flex items-center gap-2 border border-ktc-green/25 hover:border-ktc-green text-ktc-green font-medium px-8 py-3.5 rounded-md transition-colors">
                <MessageCircle size={18} /> WhatsApp Us
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
